// src/pages/CombosPage.jsx
// ------------------------------------------------------------------
// Gift Boxes — Diwali 2026.
// Five ready-packed gift boxes. Adds to the real cart (useCartStore),
// opens the cart drawer (useUIStore), or orders on WhatsApp.
// The poster on the right is drawn in code, so it always shows the
// current shop name from src/config/tenant.js.
// ------------------------------------------------------------------
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useCartStore, useUIStore } from '../store';
import { ADMIN_WHATSAPP_NUMBER, SHOP_NAME, SHOP_PHONES } from '../config/tenant';
import FireworksCanvas from '../components/fx/FireworksCanvas';
import '../components/fx/fx.css';
import './CombosPage.css';

const BRAND = SHOP_NAME;
const money = (n) => '₹' + n.toLocaleString('en-IN');
const waLink = (text) => `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

/* ---------------- GIFT BOXES ---------------- */
const GIFT_BOXES = [
  { id: 'giftbox-20', items: 20, name: 'Kids',    price: 315,  emoji: '🧒', color: '#06d6a0' },
  { id: 'giftbox-30', items: 30, name: 'Ultra',   price: 580,  emoji: '⚡', color: '#3a86ff' },
  { id: 'giftbox-40', items: 40, name: 'Hi-Tech', price: 790,  emoji: '🚀', color: '#8338ec' },
  { id: 'giftbox-50', items: 50, name: 'Wonder',  price: 970,  emoji: '✨', color: '#ff006e' },
  { id: 'giftbox-60', items: 60, name: 'Mega',    price: 1405, emoji: '👑', color: '#fb5607', top: true },
];

/* Gift box picture (SVG data URI) used as the cart thumbnail */
const giftImage = (g) => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>
  <defs><linearGradient id='b' x1='0' x2='1'><stop offset='0' stop-color='#8f1016'/><stop offset='.5' stop-color='#e63946'/><stop offset='1' stop-color='#8f1016'/></linearGradient>
  <linearGradient id='g' x1='0' x2='1'><stop offset='0' stop-color='#b8860b'/><stop offset='.5' stop-color='#ffe066'/><stop offset='1' stop-color='#b8860b'/></linearGradient></defs>
  <rect width='200' height='200' fill='#2a0f3a'/>
  <rect x='38' y='82' width='124' height='92' rx='6' fill='url(#b)'/>
  <rect x='30' y='64' width='140' height='28' rx='6' fill='#c1121f'/>
  <rect x='90' y='64' width='20' height='110' fill='url(#g)'/>
  <path d='M100 64 C70 30 50 50 70 64 Z M100 64 C130 30 150 50 130 64 Z' fill='url(#g)'/>
  <text x='100' y='142' text-anchor='middle' font-family='Arial Black,Arial' font-weight='900' font-size='30' fill='#fff' stroke='#5c0b1f' stroke-width='1.5'>${g.items}</text>
  <text x='100' y='162' text-anchor='middle' font-family='Arial' font-weight='700' font-size='13' fill='#ffe066'>ITEMS</text>
  <text x='100' y='36' text-anchor='middle' font-family='Arial Black,Arial' font-weight='900' font-size='20' fill='#ffd60a'>${g.name.toUpperCase()}</text>
</svg>`;
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
};

const giftToProduct = (g) => ({
  id: g.id,
  name: `Gift Box — ${g.items} Items (${g.name})`,
  selling_price: g.price, offer_price: null, has_offer: false,
  stock_qty: 999, tax_rate: 0,
  image_url: giftImage(g), thumb_url: giftImage(g),
  is_combo: true, combo_items: g.items,
});

/* ---------------- Poster (drawn in code) ---------------- */
function GiftPoster({ picked, onPick }) {
  return (
    <figure className="gp" aria-label={`${BRAND} Diwali 2026 gift box price list`}>
      <FireworksCanvas density={1.4} />
      <div className="gp-inner">
        <p className="gp-brand">{BRAND}</p>
        <p className="gp-year">🪔 Diwali 2026 🪔</p>
        <p className="gp-title">Gift Box</p>

        <div className="gp-table">
          <div className="gp-th"><span>ITEMS</span><span>BOX</span><span>RATE</span></div>
          {GIFT_BOXES.map((g) => (
            <button key={g.id} type="button" onClick={() => onPick(g.id)}
              className={`gp-tr ${picked === g.id ? 'is-on' : ''}`}>
              <span className="gp-n">{g.items}</span>
              <span className="gp-name">{g.name}</span>
              <span className="gp-rate">{money(g.price)}</span>
            </button>
          ))}
        </div>

        <p className="gp-phone">📞 {SHOP_PHONES[0]}</p>
      </div>
    </figure>
  );
}

export default function CombosPage() {
  const addItem  = useCartStore((s) => s.addItem);
  const openCart = useUIStore((s) => s.openCart);
  const [pickedGift, setPickedGift] = useState('giftbox-40');
  const gift = GIFT_BOXES.find((g) => g.id === pickedGift);

  const add = (g) => {
    const res = addItem(giftToProduct(g), 1);
    if (res && res.error) { toast.error(res.error); return; }
    toast.success(`Gift Box ${g.name} added to cart`);
    openCart();
  };

  const orderWA = (g) => {
    const msg = `Hello ${BRAND},\nI would like to order a Diwali 2026 Gift Box:\n\n${g.items} Items (${g.name}) — ${money(g.price)}\nQuantity: 1\n\nPlease share delivery and payment details.`;
    window.open(waLink(msg), '_blank', 'noopener');
  };

  return (
    <div className="mp-combos page-top">
      <section className="gb-stage" aria-labelledby="gb-title">
        <div className="mp-wrap gb-inner">
          <div className="gb-copy">
            <span className="gb-ribbon">🪔 Diwali 2026 special</span>
            <h1 id="gb-title" className="gb-title">Gift Box</h1>
            <p className="gb-lead">Ready-packed assorted crackers in a festive gift box. Pick a size — from 20 items (Kids) to 60 items (Mega).</p>

            <div className="gb-board" role="radiogroup" aria-label="Choose a gift box">
              <div className="gb-row gb-headrow" aria-hidden="true"><span>Item</span><span>Rate</span></div>
              {GIFT_BOXES.map((g) => (
                <button
                  key={g.id}
                  role="radio"
                  aria-checked={pickedGift === g.id}
                  className={`gb-row ${pickedGift === g.id ? 'gb-on' : ''} ${g.top ? 'gb-vip' : ''}`}
                  onClick={() => setPickedGift(g.id)}
                >
                  <span className="gb-item">
                    {g.items} Items
                    <em className="gb-tier" style={{ background: g.color }}>{g.emoji} {g.name}</em>
                  </span>
                  <span className="gb-rate">{money(g.price)}</span>
                </button>
              ))}
            </div>

            <div className="gb-buy">
              <div className="gb-sel">
                <span className="gb-sel-l">Selected</span>
                <span className="gb-sel-v">{gift.items} Items · {gift.name} · <b>{money(gift.price)}</b></span>
              </div>
              <div className="gb-btns">
                <button className="mp-act mp-a-cart" onClick={() => add(gift)}>🛒 Add to cart</button>
                <button className="mp-act mp-a-wa" onClick={() => orderWA(gift)}>💬 Order on WhatsApp</button>
              </div>
            </div>
          </div>

          <GiftPoster picked={pickedGift} onPick={setPickedGift} />
        </div>
      </section>
    </div>
  );
}
